import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "form-vehicle-search-container flex flex-col gap-6 pb-6" }
const _hoisted_2 = {
  key: 2,
  class: "flex flex-col gap-8 md:gap-6"
}
const _hoisted_3 = { class: "h-12 mt-6 md:h-min flex items-start" }
const _hoisted_4 = {
  key: 0,
  class: "mb-6 flex flex-col gap-6"
}
const _hoisted_5 = { key: 0 }

import { ref, computed, watch, nextTick } from 'vue';
import FindCarByRego from '@/components/FindCar/FindCarByRego.vue';
import FindYourCarList from '@/components/FindCar/FindYourCarList.vue';
import FindYourCar from '@/components/FindCar/FindYourCar.vue';
import { useAnalyticsStore, useJeopardyStore, useFindCarStore, useFormStore } from '@/store';
import type { IFoundVehicle } from '@/interfaces';
import ShowHideButton from '@/components/ShowHideButton/ShowHideButton.vue';
import { storeToRefs } from 'pinia';
import { Field, RichText } from '@sitecore-jss/sitecore-jss-vue';
import { ImageFieldValue } from '@sitecore-jss/sitecore-jss-vue/types/components/Image';
import PanelTitle from '@/components/PanelTitle/PanelTitle.vue';
import { FormEvents } from '@/enums/form-events.enum';
import { isMobileDevice, scrollToElement } from '@/utils/dom-helpers';
import { IAnalyticsProfileInfo } from '@/interfaces/forms/analytics-interface';
import { clone } from 'lodash-es';
import RACQFormKit from '@/components/RACQFormKit/RACQFormKit.vue';

interface IFindCarProps {
  node?: any;
  value?: any;
  topMakes: { value: string };
  regoResultsStepTitle: { value: string };
  regoResultsStepContent: { value: string };
  makeAndModelResultsStepTitle: { value: string };
  makeAndModelResultsStepContent: { value: string };
  findCarBtn: { value: string };
  regoNotFoundMessage: { value: string };
  yearLabel: { value: string };
  makeLabel: { value: string };
  modelLabel: { value: string };
  seriesLabel: { value: string };
  bodyLabel: { value: string };
  colourLabel: { value: string };
  regoInputLabel: { value: string };
  showColour: { value: boolean };
  showColourFieldOnList: { value: boolean };
  colourFieldOnListHelpText: { value: string };
  colourFieldOnListValidationText: { value: string };
  makeAndModelTitleMessage: { value: string };
  expandMakeAndModelLabel: { value: string };
  expandMakeAndModelManualLabel: { value: string };
  expandMakeAndModelPreLabel: { value: string };
  icon: Field<ImageFieldValue>;
  title: { value: string };
  subTitle: { value: string };
  roadsideRegoHelpText: { value: string };
  isRoadside: { value: boolean };
  formName: { value: string };
  tooltipContent: { value: string };
  tooltipModalTitle: { value: string };
  tooltipShowInModal: { value: boolean };
  profileInfo: IAnalyticsProfileInfo;
  lookUpVehicleManualInfoMessage: { value: string };
  showReleaseDate: { value: boolean };
}

// Define Props
// ------------------------------------------------------------- //

export default /*@__PURE__*/_defineComponent({
  __name: 'FindCar',
  props: {
    context: {}
  },
  setup(__props: any) {

const props = __props;

// Define Composables
// ------------------------------------------------------------- //
const analyticsStore = useAnalyticsStore();
const jpStore = useJeopardyStore();
const findCarStore = useFindCarStore();
const formStore = useFormStore();

// Define Component state
// ------------------------------------------------------------- //
const findYourCarEl = ref<typeof FindYourCar | undefined>(undefined);
const qldRegoFound = computed(() => findCarStore.isQldRegoFound);
const foundVehiclesByRego = ref(true);
const isExpanded = ref(false);

const vehicleRegistration = computed(() => findCarStore.registrationNumber);
const showMakeAndModel = computed(() => !qldRegoFound.value || isExpanded.value);
const showFoundVehicles = computed(() => findCarStore.showFoundVehicles);
const showExpandButton = computed(
  () => props.context.expandMakeAndModelLabel?.value && !props.context.isRoadside.value && qldRegoFound.value
);
const regoTooltip = computed(() => ({
  title: props.context?.tooltipModalTitle?.value,
  content: props.context?.tooltipContent?.value,
  tooltipId: props.context?.id + '__tooltip_id',
  tooltipOuterId: props.context?.id + '__outer',
  isModal: props.context?.tooltipShowInModal?.value,
}));
const showColourOnListView = computed(() => props.context.showColourFieldOnList?.value ?? false);
const vehicleColourOnListView = ref(findCarStore.vehicleColour);

const { selectedVehicle, foundVehicleList } = storeToRefs(findCarStore);

findCarStore.setFormName(props.context.formName.value);

// Define functions
// ------------------------------------------------------------- //
// Sync store to ref (when store updates externally)
watch(
  () => findCarStore.vehicleColour,
  (newValue: string) => {
    vehicleColourOnListView.value = newValue;
  }
);
// Sync ref to store (when user types in the input)
watch(vehicleColourOnListView, (newValue: string) => {
  findCarStore.vehicleColour = newValue;
});

function setQldRegoFound(isFound: boolean) {
  findCarStore.isQldRegoFound = isFound;
}

function setRegistration(registrationNumber: string) {
  findCarStore.registrationNumber = registrationNumber;
}

function setVehicleColour(vehicleColour: string) {
  findCarStore.vehicleColour = vehicleColour;
}

function setManualVehicleDetailsEnter(setManual: boolean) {
  findCarStore.showManualVehicleDetailsToEnter = setManual;
}

function updateFoundVehicleList(data: IFoundVehicle[], byRego?: boolean) {
  selectedVehicle.value = undefined;
  // Validate incoming data
  if (!Array.isArray(data)) {
    foundVehicleList.value = [];
  } else {
    foundVehicleList.value = data;
  }
  foundVehiclesByRego.value = byRego || false;
  findCarStore.vehicleFoundUsingRego = byRego ?? false;
  if (findCarStore.showManualVehicleDetailsToEnter) {
    isExpanded.value = false;
  }
}

function handleVehicleFoundFromRego(data: IFoundVehicle) {
  updateFoundVehicleList(data ? [data] : [], true);
  showFoundVehicleList();
  setQldRegoFound(true);
  isExpanded.value = false; // Hide make and model if vehicle found by rego
}

const toggleMakeAndModelSection = () => {
  if (!qldRegoFound.value) {
    isExpanded.value = true;
    return;
  }
  isExpanded.value = !isExpanded.value;
};

const openMakeAndModelSection = () => {
  isExpanded.value = true;
  findCarStore.resetFoundVehicleList();
  setQldRegoFound(false);
  setManualVehicleDetailsEnter(true);
};

function showFoundVehicleList() {
  formStore.clearLastStepSubmitted();
  if (foundVehicleList.value.length && foundVehicleList.value[0].manufacturingYear >= 1960) {
    findCarStore.showCarList(true);
    isMobileDevice() ? scrollToElement('body') : scrollToElement('.find-your-car-list', 500);

    /** Push event for analysis */
    if (foundVehicleList.value.length) {
      analyticsStore.pushAnalytics({
        event: FormEvents.STEP,
        form: {
          field: foundVehiclesByRego.value ? 'Found car registration' : 'Found car make and model',
          stepName: 'Find car confirmation',
          name: props.context.formName.value,
        },
        profileInfo: clone(props.context.profileInfo) || {},
      });
    }

    return;
  }

  jpStore.showJeopardyPanel({
    jpCode: findCarStore.pre1960VehicleJeopardyCode,
    areNextStepsVisible: false,
    formAnalyticsOverride: { formErrorDetail: 'We need to talk to you about your vehicle' },
  });
}

function selectVehicle(vehicle: IFoundVehicle) {
  findCarStore.selectedVehicle = vehicle;
}

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock("div", null, [
    _withDirectives(_createElementVNode("div", _hoisted_1, [
      _createVNode(PanelTitle, {
        icon: _ctx.context.icon,
        title: _ctx.context.title,
        subTitle: _ctx.context.subTitle
      }, null, 8, ["icon", "title", "subTitle"]),
      (!showFoundVehicles.value)
        ? (_openBlock(), _createBlock(FindCarByRego, {
            key: 0,
            findCarBtn: _ctx.context.findCarBtn.value,
            regoInputLabel: _ctx.context.regoInputLabel.value,
            help: _ctx.context.isRoadside?.value ? _ctx.context.roadsideRegoHelpText?.value : '',
            regoNumber: vehicleRegistration.value,
            regoTooltip: regoTooltip.value,
            showManualOverrideInfo: _unref(findCarStore).showManualVehicleDetailsToEnter,
            lookUpVehicleManualInfo: _ctx.context.lookUpVehicleManualInfoMessage?.value ?? '',
            isRoadside: _ctx.context.isRoadside?.value,
            onVehicleFound: handleVehicleFoundFromRego,
            onQldVehicleNotFound: _cache[0] || (_cache[0] = ($event: any) => (setQldRegoFound(false))),
            onSetRegistration: _cache[1] || (_cache[1] = (data) => setRegistration(data))
          }, null, 8, ["findCarBtn", "regoInputLabel", "help", "regoNumber", "regoTooltip", "showManualOverrideInfo", "lookUpVehicleManualInfo", "isRoadside"]))
        : _createCommentVNode("", true),
      (showExpandButton.value)
        ? (_openBlock(), _createBlock(ShowHideButton, {
            key: 1,
            showMore: showMakeAndModel.value,
            showLessLabel: _ctx.context.expandMakeAndModelLabel?.value,
            showMoreLabel: _ctx.context.expandMakeAndModelLabel?.value,
            appendClass: "!mt-0",
            onClick: toggleMakeAndModelSection
          }, null, 8, ["showMore", "showLessLabel", "showMoreLabel"]))
        : _createCommentVNode("", true),
      (showMakeAndModel.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (showExpandButton.value)
              ? (_openBlock(), _createBlock(_unref(RichText), {
                  key: 0,
                  field: _ctx.context.makeAndModelTitleMessage
                }, null, 8, ["field"]))
              : _createCommentVNode("", true),
            _createVNode(_component_FormKit, {
              type: "form",
              onSubmit: showFoundVehicleList
            }, {
              default: _withCtx(({ state: { valid } }) => [
                _createVNode(FindYourCar, {
                  ref_key: "findYourCarEl",
                  ref: findYourCarEl,
                  topMakes: _ctx.context.topMakes.value,
                  colourLabel: _ctx.context.colourLabel.value,
                  yearLabel: _ctx.context.yearLabel.value,
                  makeLabel: _ctx.context.makeLabel.value,
                  modelLabel: _ctx.context.modelLabel.value,
                  seriesLabel: _ctx.context.seriesLabel.value,
                  bodyLabel: _ctx.context.bodyLabel.value,
                  showColour: _ctx.context.showColour.value,
                  isRoadside: _ctx.context.isRoadside.value,
                  onModelMakeChanged: _unref(findCarStore).resetFoundVehicleList,
                  onVehicleFound: _cache[2] || (_cache[2] = (data) => updateFoundVehicleList(data)),
                  onSetColour: _cache[3] || (_cache[3] = (data) => setVehicleColour(data))
                }, null, 8, ["topMakes", "colourLabel", "yearLabel", "makeLabel", "modelLabel", "seriesLabel", "bodyLabel", "showColour", "isRoadside", "onModelMakeChanged"]),
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(_component_FormKit, {
                    label: _ctx.context.findCarBtn.value,
                    type: "submit",
                    disabled: !valid || foundVehiclesByRego.value || !_unref(foundVehicleList).length,
                    inputClass: `btn btn-primary md:text-left`
                  }, null, 8, ["label", "disabled"])
                ])
              ]),
              _: 1
            })
          ]))
        : _createCommentVNode("", true)
    ], 512), [
      [_vShow, !showFoundVehicles.value]
    ]),
    (showFoundVehicles.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(FindYourCarList, {
            regoResultsStepTitle: _ctx.context.regoResultsStepTitle.value,
            regoResultsStepContent: _ctx.context.regoResultsStepContent.value,
            makeAndModelResultsStepTitle: _ctx.context.makeAndModelResultsStepTitle.value,
            makeAndModelResultsStepContent: _ctx.context.makeAndModelResultsStepContent.value,
            vehicleList: _unref(foundVehicleList),
            foundVehiclesByRego: foundVehiclesByRego.value,
            showReleaseDate: _ctx.context.showReleaseDate?.value ?? false,
            onVehicleSelected: selectVehicle
          }, null, 8, ["regoResultsStepTitle", "regoResultsStepContent", "makeAndModelResultsStepTitle", "makeAndModelResultsStepContent", "vehicleList", "foundVehiclesByRego", "showReleaseDate"]),
          (showColourOnListView.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(RACQFormKit, {
                  modelValue: vehicleColourOnListView.value,
                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((vehicleColourOnListView).value = $event)),
                  bindings: {
            help: _ctx.context.colourFieldOnListHelpText.value || '',
            innerClass: 'input-sm',
            label: 'Vehicle colour',
            type: 'text',
            validation: [['required', 'trim']],
            validationMessages: {
              required: _ctx.context.colourFieldOnListValidationText?.value || 'Please enter a vehicle colour',
            },
            maxlength: 100,
          }
                }, null, 8, ["modelValue", "bindings"])
              ]))
            : _createCommentVNode("", true),
          (!qldRegoFound.value && showFoundVehicles.value)
            ? (_openBlock(), _createBlock(_unref(RichText), {
                key: 1,
                field: _ctx.context.regoNotFoundMessage
              }, null, 8, ["field"]))
            : _createCommentVNode("", true),
          (foundVehiclesByRego.value)
            ? (_openBlock(), _createBlock(ShowHideButton, {
                key: 2,
                showMore: showMakeAndModel.value,
                showLessLabel: _ctx.context.expandMakeAndModelManualLabel?.value,
                showMoreLabel: _ctx.context.expandMakeAndModelManualLabel?.value,
                showMorePreCopy: _ctx.context.expandMakeAndModelPreLabel?.value,
                isDropDownVariant: false,
                appendClass: "!mt-0",
                onClick: openMakeAndModelSection
              }, null, 8, ["showMore", "showLessLabel", "showMoreLabel", "showMorePreCopy"]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})